"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { DollarSign, Percent } from "lucide-react";
import NextImage from "next/image";
import Image from "@/helpers/Image";
import goldLeaf from "@/public/anniversary/goldLeaf.svg";
import { PageFuturesLPSchema } from "@/sanity/pages/PageFuturesLPTemplate";

const BADGE_ICONS = [
  <DollarSign
    key="dollar"
    className="w-[3rem] h-[3rem] text-white"
    strokeWidth={1.5}
  />,
  <Percent
    key="percent"
    className="w-[3rem] h-[3rem] text-white"
    strokeWidth={1.5}
  />,
];

const BADGE_POSITIONS = [
  "top-[62%] -left-[1rem] lg:-left-[3rem]",
  "top-[80%] -right-[1rem] lg:-right-[3rem]",
];

const descriptionComponents = {
  ...richTextComponent,
  marks: {
    ...(richTextComponent.marks as Record<string, unknown>),
    strong: ({ children }: { children?: React.ReactNode }) => (
      <strong className="font-medium text-black">{children}</strong>
    ),
  },
};

export default function HeroSection({ data }: { data: PageFuturesLPSchema }) {
  return (
    <section className="bg-white pt-[8rem] lg:pt-[10rem]">
      <div className="mx-auto w-[min(150rem,100%)] px-[3rem] lg:px-[5rem] flex flex-col">
        {/* Two-column content */}
        <div className="flex flex-col lg:flex-row gap-[4rem] lg:gap-[8rem] items-start pb-[6rem] lg:pb-[8rem]">
          {/* Left column */}
          <div className="flex-1 w-full flex flex-col gap-[3rem] lg:gap-[4.5rem]">
            {/* Title block */}
            <div className="flex flex-col ">
              {data.heroChip && (
                <p className="mb-0 font-blacker italic text-[1.875rem] leading-[1.5] text-black">
                  {data.heroChip}
                </p>
              )}
              {!!data.heroTitle?.length && (
                <div className="richtext richtext-start flex flex-col gap-0 prose-h1:text-[5rem] lg:prose-h1:text-[7rem] mb-[2rem]">
                  <PortableText
                    value={data.heroTitle}
                    components={richTextComponent}
                  />
                </div>
              )}
              {!!data.heroDescription && (
                <div
                  className="font-geist text-[2.25rem] leading-[1.5] text-black/70 [&_br]:hidden lg:[&_br]:block"
                  style={{ textWrapStyle: "pretty" }}
                >
                  <PortableText
                    value={data.heroDescription}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>

            {/* Pricing card */}
            <div className="relative bg-[#f7f7f7] overflow-hidden flex flex-col gap-[3rem] p-[3rem] w-full max-w-[60rem]">
              {/* Green top accent line */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[0.375rem] w-[54%] bg-[#003828]" />

              {/* Price row */}
              <div className="flex items-start justify-between gap-[2rem] whitespace-nowrap">
                <div className="flex flex-col gap-[0.5rem] leading-[1.3]">
                  <p className="mb-0 font-geist font-semibold text-[#003828] text-[6rem] lg:text-[6.75rem] leading-none tracking-[-0.2rem]">
                    {data.heroPrice ?? "$9"}
                  </p>
                  <p className="mb-0 font-geist font-normal line-through text-[2.25rem] lg:text-[2.75rem] text-black tracking-[-0.0825rem]">
                    {data.heroOriginalPrice ?? "Regulär: 89 $"}
                  </p>
                </div>
                <div className="flex flex-col gap-[1rem] font-geist font-normal text-[1.85rem] lg:text-[2rem] leading-[1.2] text-black tracking-[-0.04rem] pt-[1rem]">
                  {(data.heroPriceDetails ?? []).map((item, i) => (
                    <p key={item._key ?? i} className="mb-0 opacity-60">
                      {item.text}
                    </p>
                  ))}
                </div>
              </div>

              {/* CTA + payment methods */}
              <div className="flex flex-col gap-[1.5rem]">
                {data.heroCTA && (
                  <CTAButton data={data.heroCTA} className="w-full" />
                )}
                {(data.heroPaymentLabel || !!data.heroPaymentLogos?.length) && (
                  <div className="flex flex-col gap-[0.75rem] items-center">
                    {data.heroPaymentLabel && (
                      <p className="mb-0 font-geist font-medium text-[1.5rem] leading-[1.5] text-[#001b14] opacity-50 text-center tracking-[-0.03rem] uppercase">
                        {data.heroPaymentLabel}
                      </p>
                    )}
                    {!!data.heroPaymentLogos?.length && (
                      <div className="flex gap-[1.5rem] items-center justify-center opacity-50 flex-wrap">
                        {data.heroPaymentLogos.map((logo, i) => (
                          <Image
                            key={i}
                            src={logo}
                            alt=""
                            className="!h-[4.25rem] !w-auto object-contain shrink-0"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right column — portrait image + badges */}
          <div className="w-full lg:w-[61.5rem] shrink-0 flex flex-col">
            <div className="relative w-full aspect-[481/602]">
              <div className="absolute inset-0 overflow-hidden">
                {data.heroImage && (
                  <Image
                    src={data.heroImage}
                    alt=""
                    className="!w-full !h-full object-cover object-center"
                  />
                )}
              </div>

              {/* Floating stat badges */}
              {data.heroImageBadges?.map((badge, i) => (
                <div
                  key={badge._key ?? i}
                  className={`absolute ${BADGE_POSITIONS[i] ?? "top-[80%] left-[5%]"} flex gap-[1.625rem] items-center bg-[#003828] border border-black/10 px-[1.125rem] py-[1rem]`}
                >
                  <div className="bg-[rgba(18,240,180,0.1)] flex items-center justify-center p-[1rem] shrink-0 w-[5rem] h-[5rem]">
                    {BADGE_ICONS[i] ?? BADGE_ICONS[0]}
                  </div>
                  <div className="font-geist-mono font-semibold text-[1.75rem] leading-[1.33] text-white uppercase whitespace-nowrap">
                    {badge.value && <p className="mb-0">{badge.value}</p>}
                    {badge.label && <p className="mb-0">{badge.label}</p>}
                  </div>
                </div>
              ))}
            </div>

            {/* Caption bar */}
            {data.heroImageCaption && (
              <div className="bg-[#001b14] flex items-center justify-center px-[2rem] py-[1rem]">
                <div className="richtext richtext-center prose-p:font-geist prose-p:font-medium prose-p:text-[2.25rem] prose-p:leading-[1.2] prose-p:text-[#d6eee7] prose-p:whitespace-nowrap prose-p:tracking-[0.05em]">
                  <PortableText
                    value={data.heroImageCaption}
                    components={richTextComponent}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats bar */}
        {!!data.heroStats?.length && (
          <div className="border border-black/10 flex flex-wrap lg:flex-nowrap items-stretch">
            {data.heroStats.map((stat, i) => (
              <div
                key={stat._key ?? i}
                className="w-1/2 lg:flex-1 flex items-center justify-center gap-[0.5rem] lg:gap-[1.5rem] px-[1.5rem] py-[3rem] lg:py-[5rem]"
              >
                <NextImage
                  src={goldLeaf}
                  alt=""
                  width={33}
                  height={48}
                  className="flex-shrink-0"
                />
                <div className="flex flex-col items-center text-center gap-[0.5rem]">
                  <p className="mb-0 font-geist font-medium text-[2.5rem] lg:text-[3rem] leading-none text-[#003828] tracking-[-0.15rem]">
                    {stat.value}
                  </p>
                  <p className="mb-0 font-geist font-normal text-[2rem] leading-[1.5] text-black/70 tracking-[-0.1rem]">
                    {stat.label}
                  </p>
                </div>
                <NextImage
                  src={goldLeaf}
                  alt=""
                  width={33}
                  height={48}
                  className="flex-shrink-0 scale-x-[-1]"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
